import 'whatwg-fetch';
import isEmpty from 'lodash/isEmpty';
import {getToken} from "./userUtility";
import {buildQueryParams, go} from "./navigationUtility";
import appStore from '../containers/app/store';
import {ErrorType} from "../constants/enums";

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response) {
    return response.json();
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
async function checkStatus(response) {
    if (response.status >= 200 && response.status < 300) {
        return response;
    }
    if (response.status === 401) {
        go('/signin');
        return;
    }
    let res;
    try {
        res = await response.json();
    } catch (err) {
        res = err;
    } finally {
        if (response.status === 404) {
            appStore.setError({type: ErrorType.NotFound, desc: 'Page not found', res: res});
        } else if (response.status === 400) {
            appStore.createAlertFromMessageId('error', res.err ? res.err : 'notifications.error', 4000);
        } else {
            appStore.setError({type: ErrorType.General, desc: '', res: res});
        }
        throw res;
    }
}

/**
 * Build the query params to be set on the url
 * @param params    {Object}  The payload object
 * @param url   {String}    The api destination url
 * @returns {string}    the formatted query params
 */
function setQueryParams(params = {}, url) {
    if (!isEmpty(params.method)) {
        return '';
    }
    return (!isEmpty(params)) ? '?' + buildQueryParams(params) : '';
}

/**
 * Process the request options and return the options to send to the server
 * @param options {Object} Request options from the api call
 * @returns {*}
 */
function processRequestOptions(options = {}) {
    let baseOptions = {
        credentials: 'same-origin',
        mode: 'cors',
        headers: {
            "Content-Type": "application/json",
            "Authorization": getToken()
        }
    };
    //parse all options
    if (!isEmpty(options)) {
        if (isEmpty(options.method)) {
            return baseOptions;
        } else {
            options.method = options.method.toUpperCase();
        }
        if (!isEmpty(options.headers)) {
            options.headers = Object.assign({}, baseOptions.headers, options.headers);
        }
        if (!isEmpty(options.body)) {
            options.body = JSON.stringify(options.body);
        }
    }
    return Object.assign(baseOptions, options);

}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [payload] The payload we want to pass to "fetch"
 * @return {object}           The response data
 */
async function request(url, payload = {}, ignoreError = false) {
    let requestUrl = `${process.env.REACT_APP_BASE_URL}${url}${setQueryParams(payload, url)}`;
    try {
        let response = await fetch(requestUrl, processRequestOptions(payload));
        if (!ignoreError) {
            checkStatus(response);
        }
        return parseJSON(response);
    } catch (err) {
        if (!ignoreError) {
            console.log('error during request:', err);
            appStore.setError({type: ErrorType.General, desc: '', res: err});
        }
    }
}

export async function requestAudioFile(key, progressCallback) {
    let requestUrl = `/audio/${key}`;

        return new Promise((resolve, reject) => {
            let req = new XMLHttpRequest();

            req.onload = (e) => {
                resolve(req.response);
            };

            req.addEventListener('progress', progressCallback);

            req.addEventListener('error', (e) => {
                reject(e);
            });
            req.addEventListener('abort', (e) => {
                reject(e);
            });

            req.open("GET", requestUrl);
            req.setRequestHeader('Authorization', getToken());
            req.responseType = 'arraybuffer';

            req.send();
        });

}

export default request;
